import { Emitter } from "../../../../base/common/events";
import { IStartup } from "../../../../base/common/lifecycle";
import { ILogService, LogLevel } from "../../../../platform/log/common/log";
import { ISecretStorageService } from "../../../../platform/secrets/common/secrets";
import { IStorageService, StorageScope, StorageTarget } from "../../../../platform/storage/common/storage";
import { IUserConfiguration, UserConfigurationChangeEvent } from "./userConfiguration";

export class UserConfiguration implements IUserConfiguration, IStartup {

    private readonly _onDidChange = new Emitter<UserConfigurationChangeEvent>();
    readonly onDidChange = this._onDidChange.event;
    private notifyChanged() {
        this._onDidChange.fire({});
    }


    constructor(
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
        @ISecretStorageService private readonly secretStorageService: ISecretStorageService,
    ) {

        const storedLogLevel = this.storageService.get("log.level", StorageScope.PROFILE);
        if (storedLogLevel) {
            const level = LogLevel[storedLogLevel as keyof typeof LogLevel];
            this.logger.setLevel(level);
        }
    }



    initialize(): Promise<void> {
        return Promise.resolve();
    }


    // #region log level
    canChangeLogLevel: boolean = false;
    get logLevel(): LogLevel {
        const level = this.logger.getLevel();
        return level;
    }
    set logLevel(level: LogLevel) {
        const current = this.logger.getLevel();
        if (current === level) return;
        this.storageService.store("log.level", LogLevel[level], StorageScope.PROFILE, StorageTarget.USER);
        this.logger.setLevel(level);
        this.notifyChanged();
    }
    get availableLogLevels(): Readonly<Record<LogLevel, string>> {
        return logLevelOptions;
    }



    // #region theme
    get theme(): string {
        return this.storageService.get("ui.theme", StorageScope.APPLICATION) || "default";
    }
    set theme(value: string) {
        if (value === this.theme) return;
        this.storageService.store("ui.theme", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }

    get hierarchyFilterInactive(): boolean {
        return this.storageService.getBoolean("hierarchy.filterMode.inactive", StorageScope.APPLICATION, false) ?? false;
    }
    set hierarchyFilterInactive(value: boolean) {
        if (value === this.hierarchyFilterInactive) return;
        this.storageService.store("hierarchy.filterMode.inactive", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }

    get richTooltip() {
        return this.storageService.getBoolean("ui.rich-tooltip", StorageScope.APPLICATION, true) ?? true;
    }
    set richTooltip(value: boolean) {
        if (value === this.richTooltip) return;
        this.storageService.store("ui.rich-tooltip", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }


    // #region ui layout
    get uiLayout(): "default" | "minimal" {
        return this.storageService.get("ui.layout-style", StorageScope.APPLICATION) as "default" | "minimal" || "default";
    }
    set uiLayout(value: "default" | "minimal") {
        if (value === this.uiLayout) return;
        this.storageService.store("ui.layout-style", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region webgl scene picking
    get webglPicking(): boolean {
        return this.storageService.getBoolean("webgl.picking", StorageScope.APPLICATION) ?? true;
    }
    set webglPicking(value: boolean) {
        if (value === this.webglPicking) return;
        this.storageService.store("webgl.picking", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get propertyAutoRefresh(): boolean {
        return this.storageService.getBoolean("property.auto-refresh", StorageScope.APPLICATION, true) ?? true;
    }
    set propertyAutoRefresh(value: boolean) {
        if (value === this.propertyAutoRefresh) return;
        this.storageService.store("property.auto-refresh", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get showSupportButton(): boolean {
        return this.storageService.getBoolean("ui.show-support-button", StorageScope.APPLICATION, true) ?? true;
    }
    set showSupportButton(value: boolean) {
        if (value === this.showSupportButton) return;
        this.storageService.store("ui.show-support-button", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    

    // #region panel collapsed state
    setPanelVisible(panel: string, collapsed: boolean): void {
        const current = this.isPanelVisible(panel);
        if (current === collapsed) return;
        this.storageService.store(`ui.panel.${panel}.visible`, collapsed, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }
    isPanelVisible(panel: string, fallbackValue?: boolean): boolean {
        const val = this.storageService.getBoolean(`ui.panel.${panel}.visible`, StorageScope.APPLICATION);
        if (typeof val === "boolean") {
            return val;
        }
        return fallbackValue ?? true;
    }

    // #region panel collapsed state
    setPanelCollapsed(panel: string, collapsed: boolean): void {
        const current = this.isPanelCollapsed(panel);
        if (current === collapsed) return;
        this.storageService.store(`ui.panel.${panel}.collapsed`, collapsed, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }
    isPanelCollapsed(panel: string, fallbackValue?: boolean): boolean {
        const val = this.storageService.getBoolean(`ui.panel.${panel}.collapsed`, StorageScope.APPLICATION);
        if (typeof val === "boolean") {
            return val;
        }
        return fallbackValue ?? false;
    }


    get aiApiKey(): Promise<string | null | undefined> {
        return this.secretStorageService.get("ai.api-key");
    }
    set aiApiKey(value: string | null | undefined) {
        if (value) {
            this.secretStorageService.set("ai.api-key", value);
        }
        else {
            this.secretStorageService.delete("ai.api-key");
        }
    }

    async readAiApiKey(): Promise<string | null> {
        const key = await this.secretStorageService.get("ai.api-key") || null;
        return key;
    }
    async storeAiApiKey(key: string): Promise<void> {
        await this.secretStorageService.set("ai.api-key", key);
    }
}


const logLevelOptions: Readonly<Record<LogLevel, string>> = {
    [LogLevel.Off]: "OFF",
    [LogLevel.Trace]: "TRACE",
    [LogLevel.Debug]: "DEBUG",
    [LogLevel.Info]: "INFO",
    [LogLevel.Warning]: "WARNING",
    [LogLevel.Error]: "ERROR",
};